// ============================================================================
// FSA ENGAGEMENTS LIST SCREEN
// ============================================================================
// Sidebar entry into the FSA module — one row per engagement that has FSA
// configured (i.e. a row in fsa.tbl_fsa_engagement_settings). Selecting a
// row opens that engagement's FSA workspace tab via the module-navigation
// bridge.
//
// Backend: GET /api/v1/FsaEngagements → PagedResult<FsaEngagementSettings>.
// Hook:    useFsaEngagements(params).
//
// Per prima-web/CLAUDE.md Module Boundaries: FSA screens live under
// src/components/modules/fsa/.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useFsaEngagements } from 'hooks/api/fsa/useFsaEngagementSettings';
import { useClients } from 'hooks/api/useClient';
import { useEngagementTypes } from 'hooks/api/useEngagementTypes';
import { fsaTemplateService } from 'api/services/fsa/fsaTemplateService';
import type {
  FsaEngagementSettings,
  FsaEngagementListSearchRequest,
} from 'api/types/fsa/engagementSettings.types';
import { openModule } from 'services/moduleNavigation';
import FsaEngagementSettingsModal from 'components/modals/modules/fsa/FsaEngagementSettingsModal';

// View mode for the master grid — mirrors the enterprise list pattern
// (see EngagementMasterScreen): toolbar offers list ↔ grid card toggle.
type ViewMode = 'list' | 'grid';

// Column definitions powering the ColumnSelector (toolbar gear menu).
// `required: true` columns can't be hidden; `defaultVisible` controls the
// initial render. Persisted to localStorage under COLUMN_PREF_KEY.
const columnDefinitions: ColumnDefinition[] = [
  { id: 'clientName',       label: 'Client',        field: 'clientName',          required: true,  defaultVisible: true,  sortable: true },
  { id: 'engagementCode',   label: 'Engagement',    field: 'engagementCode',      required: true,  defaultVisible: true,  sortable: true },
  { id: 'engagementType',   label: 'Type',          field: 'engagementTypeName',  required: false, defaultVisible: true,  sortable: true },
  { id: 'year',             label: 'FY',            field: 'engagementYear',      required: false, defaultVisible: true,  sortable: true },
  { id: 'template',         label: 'Template',      field: 'templateCode',        required: false, defaultVisible: true,  sortable: true },
  { id: 'financialYearEnd', label: 'FY End',        field: 'financialYearEnd',    required: false, defaultVisible: true,  sortable: true },
  { id: 'currency',         label: 'Currency',      field: 'currencyCode',        required: false, defaultVisible: true,  sortable: false },
  { id: 'rounding',         label: 'Rounding',      field: 'roundingFactorLabel', required: false, defaultVisible: true,  sortable: false },
  { id: 'status',           label: 'Status',        field: 'statusName',          required: false, defaultVisible: true,  sortable: true },
  { id: 'updatedAt',        label: 'Last Updated',  field: 'updatedAt',           required: false, defaultVisible: true,  sortable: true },
];

const COLUMN_PREF_KEY = 'fsa-engagement-list-columns';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

interface FsaEngagementListScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const FsaEngagementListScreen: React.FC<FsaEngagementListScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  void tabId;
  void moduleName;

  // Server-side query state
  const [queryParams, setQueryParams] = useState<FsaEngagementListSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: false,
  });
  const { data, pagination, loading, refetch } = useFsaEngagements(queryParams);

  // View state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Column visibility — persisted to localStorage (per the enterprise list
  // pattern). Defaults to every defaultVisible column on first load.
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // Filter lookups — load clients, engagement types, and FSA templates so the
  // filter panel can present SyutSelect dropdowns (matching the Engagement
  // Master pattern). Filter values are name/code strings — the backend uses
  // ILIKE so picking from the dropdown does an exact-match against the same
  // column the grid renders.
  const { data: clientsRaw } = useClients({ page: 1, pageSize: 500, includeInactive: false });
  const { data: engagementTypesRaw } = useEngagementTypes({ page: 1, pageSize: 500, includeInactive: false });
  const [templateOptionsRaw, setTemplateOptionsRaw] = useState<{ id: string; code: string; name: string }[]>([]);

  useEffect(() => {
    fsaTemplateService
      .getAll({ page: 1, pageSize: 100 })
      .then((r) => {
        setTemplateOptionsRaw(r.data.map((t) => ({ id: String(t.id), code: t.code ?? '', name: t.name ?? '' })));
      })
      .catch(() => { /* lookup is best-effort */ });
  }, []);

  // "All" sentinel = empty string ⇒ filter not applied. FSA list filters are
  // name/code based (backend ILIKE), so the value sent matches the column.
  const clientFilterOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      ...clientsRaw.map((c) => ({ value: c.legalName || c.name || '', label: c.legalName || c.name || '—' })),
    ],
    [clientsRaw]
  );
  const engagementTypeFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...engagementTypesRaw.map((et) => ({ value: et.name, label: et.name }))],
    [engagementTypesRaw]
  );
  const templateFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...templateOptionsRaw.map((t) => ({ value: t.code, label: `${t.code} · ${t.name}` }))],
    [templateOptionsRaw]
  );

  // Filter inputs (drafts — pushed to queryParams on Apply)
  const [clientNameFilter, setClientNameFilter] = useState('');
  const [engagementCodeFilter, setEngagementCodeFilter] = useState('');
  const [engagementTypeNameFilter, setEngagementTypeNameFilter] = useState('');
  const [yearFilter, setYearFilter] = useState<string>('');
  const [templateCodeFilter, setTemplateCodeFilter] = useState('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({ ...prev, search: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // "Configure FSA" modal — opens the existing settings modal in create
  // mode with no pre-selected engagement, so the modal renders its
  // engagement picker (populated from GET /api/v1/FsaEngagements/eligible).
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Open FSA workspace for the selected engagement. Per-instance tab keyed
  // by engagement id — opening different engagements yields distinct tabs;
  // re-opening the same one refocuses the existing tab.
  const handleOpenWorkspace = useCallback((record: FsaEngagementSettings) => {
    const clientLabel = record.clientName?.trim() || 'Engagement';
    const code = record.engagementCode?.trim();
    const tabTitle = code
      ? `${clientLabel} (${code}) · FSA`
      : `${clientLabel} · FSA`;
    openModule(
      'fsa-engagement',
      { engagementId: record.primaEngagementId },
      { instanceKey: String(record.primaEngagementId), tabTitle },
    );
  }, []);

  // Column definitions
  const tableColumns: SyutGridColumn<FsaEngagementSettings>[] = useMemo(
    () => [
      {
        id: 'clientName',
        header: 'Client',
        accessorKey: 'clientName',
        size: 220,
        enableSorting: true,
        cell: ({ row }) => row.original.clientName || '—',
      },
      {
        id: 'engagementCode',
        header: 'Engagement',
        accessorKey: 'engagementCode',
        size: 140,
        enableSorting: true,
        cell: ({ row }) => row.original.engagementCode || '—',
      },
      {
        id: 'engagementType',
        header: 'Type',
        accessorKey: 'engagementTypeName',
        size: 160,
        enableSorting: true,
        cell: ({ row }) => row.original.engagementTypeName || '—',
      },
      {
        id: 'year',
        header: 'FY',
        accessorKey: 'engagementYear',
        size: 80,
        enableSorting: true,
        cell: ({ row }) => row.original.engagementYear ?? '—',
      },
      {
        id: 'template',
        header: 'Template',
        accessorKey: 'templateCode',
        size: 220,
        enableSorting: true,
        cell: ({ row }) => (
          <span title={row.original.templateName ?? ''}>
            {row.original.templateCode || '—'}
          </span>
        ),
      },
      {
        id: 'financialYearEnd',
        header: 'FY End',
        accessorKey: 'financialYearEnd',
        size: 120,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.financialYearEnd),
      },
      {
        id: 'currency',
        header: 'Currency',
        accessorKey: 'currencyCode',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => row.original.currencyCode || '—',
      },
      {
        id: 'rounding',
        header: 'Rounding',
        accessorKey: 'roundingFactorLabel',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => row.original.roundingFactorLabel || '—',
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'updatedAt',
        header: 'Last Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt ?? row.original.createdAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<FsaEngagementSettings>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  // Row actions — "Open FSA" navigates to the engagement workspace.
  const getRowActions = useCallback(
    (row: FsaEngagementSettings): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('view') || allowedActions.includes('open')) {
        items.push({
          id: 'open',
          label: 'Open FSA',
          icon: <IconSystem.chevronRight size={16} />,
          onClick: () => handleOpenWorkspace(row),
        });
      }
      return items;
    },
    [allowedActions, handleOpenWorkspace]
  );

  // Action buttons — Add (opens settings modal with picker) + Refresh.
  // The Add button is the single entry point for configuring FSA on an
  // engagement; the basic-tier Engagement screen no longer carries this
  // action (cleaner module separation).
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') || allowedActions.includes('create') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Configure FSA for an engagement',
        onClick: () => setAddModalOpen(true),
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, refetch]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.clientName) count++;
    if (queryParams.engagementCode) count++;
    if (queryParams.engagementTypeName) count++;
    if (queryParams.year !== undefined) count++;
    if (queryParams.templateCode) count++;
    return count;
  }, [queryParams.clientName, queryParams.engagementCode, queryParams.engagementTypeName, queryParams.year, queryParams.templateCode]);

  const applyFilters = useCallback(() => {
    const yearNum = yearFilter ? Number(yearFilter) : undefined;
    setQueryParams((prev) => ({
      ...prev,
      clientName: clientNameFilter || undefined,
      engagementCode: engagementCodeFilter || undefined,
      engagementTypeName: engagementTypeNameFilter || undefined,
      year: yearNum && !Number.isNaN(yearNum) ? yearNum : undefined,
      templateCode: templateCodeFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [clientNameFilter, engagementCodeFilter, engagementTypeNameFilter, yearFilter, templateCodeFilter]);

  const clearFilters = useCallback(() => {
    setClientNameFilter('');
    setEngagementCodeFilter('');
    setEngagementTypeNameFilter('');
    setYearFilter('');
    setTemplateCodeFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: false,
      search: searchQuery || undefined,
    }));
  }, [searchQuery]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="FSA Engagements"
        description="Engagements with FSA configured. Open one to enter its workspace."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.chart size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search client, engagement, type, template…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data as any}
                columns={visibleTableColumns as any}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={getRowActions as any}
                frozenRightColumns={['actions']}
                emptyMessage={loading ? 'Loading FSA engagements…' : 'No engagements have FSA configured yet. Click Add to configure one.'}
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {loading ? 'Loading FSA engagements…' : 'No engagements have FSA configured yet. Click Add to configure one.'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          cursor: 'pointer',
                        }}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOpenWorkspace(item)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOpenWorkspace(item);
                          }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                              {item.engagementCode || '—'}
                            </div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {item.clientName || '—'}
                            </div>
                          </div>
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {item.engagementTypeName || '—'} · FY {item.engagementYear ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          Template: {item.templateCode || '—'}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          <span>FY End: {formatDate(item.financialYearEnd)}</span>
                          <span>·</span>
                          <span>{item.currencyCode || '—'}</span>
                          <span>·</span>
                          <span>{item.roundingFactorLabel || '—'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel — sibling of the inner flex wrapper (not nested),
            conditionally rendered. Matches the FSA Template screen layout
            so the panel collapses cleanly when filterOpen is false. */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Client"
                  options={clientFilterOptions}
                  value={clientNameFilter}
                  onChange={(val) => setClientNameFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Engagement Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={engagementCodeFilter}
                  onChange={(e) => setEngagementCodeFilter(e.target.value)}
                  placeholder="Filter by code…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Engagement Type"
                  options={engagementTypeFilterOptions}
                  value={engagementTypeNameFilter}
                  onChange={(val) => setEngagementTypeNameFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Year</label>
                <input
                  type="number"
                  className="form-input"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  placeholder="e.g. 2025"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Template"
                  options={templateFilterOptions}
                  value={templateCodeFilter}
                  onChange={(val) => setTemplateCodeFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Configure FSA — modal renders the engagement picker because
          engagementId is null. On save the grid refreshes (which moves
          the just-configured engagement into the list). */}
      <FsaEngagementSettingsModal
        isOpen={addModalOpen}
        mode="create"
        engagementId={null}
        existing={null}
        onClose={() => setAddModalOpen(false)}
        onSaved={() => {
          setAddModalOpen(false);
          refetch();
        }}
      />
    </div>
  );
};

export default FsaEngagementListScreen;
